import { type ApolloClient } from "@apollo/client";
import { type ApolloQueryResult } from "@apollo/client/core/types";
import { EMPTY, type Observable, from, of } from "rxjs";
import { map, withLatestFrom } from "rxjs/operators";

import { GetLensesType, MyLensesSortDirection, SortBy } from "../../gql-generated/types";
import {
    type GetTopLensesInfoQuery,
    type GetTopLensesInfoQueryVariables,
    type GetTopLensesQuery,
    getTopLensesInfo,
} from "../api/queries";
import { type LensCard, type TopLensCardState } from "../models";

import { DEFAULT_NUMBER_OF_TOP_LENSES } from "./createTopLensCardState";
import { mergeTopLensesCards } from "./mergeTopLensesCards";

const LENSES_LIMIT = 10;
const OFFSET_MAX_LIMIT = 50;

export const getLensDetails = (
    client: ApolloClient<unknown>,
    topLensesStats$: Observable<GetTopLensesQuery | undefined>,
    state: TopLensCardState,
): Observable<TopLensCardState> => {
    const { numberOfTopLenses, offset, unlockableIdList, isFilled, isEmpty } = state;

    if (isFilled || isEmpty) {
        return EMPTY;
    }

    if (numberOfTopLenses === 0) {
        return of<TopLensCardState>({ ...state, isEmpty: true });
    }

    if (offset === OFFSET_MAX_LIMIT) {
        return of<TopLensCardState>({ ...state, isFilled: true });
    }

    const nextOffset = offset + LENSES_LIMIT;
    const selectedUnlockableIdList = unlockableIdList.slice(offset, nextOffset);

    return from(
        client.query<GetTopLensesInfoQuery, Partial<GetTopLensesInfoQueryVariables>>({
            query: getTopLensesInfo,
            variables: {
                filterBy: {
                    unlockableIdList: selectedUnlockableIdList,
                    lockedList: [],
                    nameList: [],
                    statusList: [],
                    surfaceTypeList: [],
                },
                type: GetLensesType.Community,
                limit: LENSES_LIMIT,
                offset: 0,
                sortBy: SortBy.SortByUnset,
                sortDirection: MyLensesSortDirection.SortDirectionUnset,
            },
        }),
    ).pipe(
        withLatestFrom(topLensesStats$),
        map<[ApolloQueryResult<GetTopLensesInfoQuery>, GetTopLensesQuery | undefined], LensCard[]>(
            ([{ data: getLenses }, getTopLenses]) => {
                return mergeTopLensesCards(
                    state,
                    getLenses,
                    getTopLenses?.getMyLensesOrganicStats.responseEntryList.slice(offset, nextOffset) ?? [],
                );
            },
        ),
        map((nextLensCards) => {
            const isEmpty = nextLensCards.length === 0;
            const isFilled =
                nextLensCards.length === numberOfTopLenses || numberOfTopLenses < DEFAULT_NUMBER_OF_TOP_LENSES;

            return {
                ...state,
                lensCards: nextLensCards,
                offset: nextOffset,
                isFilled,
                isEmpty,
            };
        }),
    );
};
